import React, {
  createRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  callApi,
  fnGetDataSet,
  fnGetTableById,
  fnSave,
} from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  CalAmBulk,
  getInitFormData,
  getNoSlip,
  getPlanDt,
  setConfigToColumns,
  setUmByFgUm,
  toKeyMap,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, globalMenu, withMenuLoading } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import { showPopupModal } from "@widgets/CommonModal";
import {
  CellValueChangedEvent,
  SnGridRef,
} from "@shared/components/GridV2/SnGridTypes";
import {
  buildJsAddinfoFromFlags,
  extractAddInfoFlags,
  hydrateRowsForExcelAddInfo,
  hydrateRowsWithAddInfo,
} from "@shared/utils/AddInfoUtils";
import {
  ExcelUpload_S01_Validate,
  openS02,
  SlipNumberingFn,
  toNonEmptyString,
} from "@widgets/commonpopup/excel-upload";
import { dateToString } from "@shared/utils/DateUtils";
import { fnDrilldown } from "@shared/utils/DrilldownUtils";
import { RowNode } from "ag-grid-community";
import SN_SAL_SAO_REG_001_POP_001 from "@menu/SAL/SN_SAL_SAO_REG_001/SN_SAL_SAO_REG_001_POP_001";
import SN_SAL_SAO_REG_001_POP_002 from "@menu/SAL/SN_SAL_SAO_REG_001/SN_SAL_SAO_REG_001_POP_002";
import ComCreateUi from "@menu/COM/ComCreateUi";
import { openAddInfoPopup } from "@widgets/commonpopup/sys/openAddInfoPopup";
import { openAddInfoRegPopup } from "@widgets/commonpopup/sys/openAddInfoRegPopup";
import layout from "./SN_SAL_SAO_REG_001.json";
import {
  executeApprovalSubmit,
  executeApprovalView,
} from "@shared/utils/approval/approvalIntegration";
import { submitApproval, viewApproval } from "@shared/api/approvalSubmitApi";

const getNoSlipStrict: SlipNumberingFn = async (
  cd_module,
  no_line,
  cd_prefix,
  date,
  cd_menu
) =>
  toNonEmptyString(
    await getNoSlip(cd_module, no_line, cd_prefix, date, cd_menu),
    "전표번호가 비어 있습니다."
  );

interface Props {
  Menuprops: menuInfo;
}

// 수주등록
const SN_SAL_SAO_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();

  // 전자결재 연동 여부
  const isApprovalVisible = config?.MAS0121?.dc_ctrl === "Y";

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    sheet1: React.createRef(),
  });

  const sotypeRef = useRef<any[]>([]);
  const dtIsuPlanRef = useRef<string>("");
  const erpIfRef = useRef<string[]>([]); // ERP 연동 대상 no_if

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    sheet1: [],
  });

  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    sheet1: [],
  });

  const [headerInfo, setHeaderInfo] = useState<any>({
    grid1: {},
    grid2: {},
  });

  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedDtDocu, setSelectedDtDocu] = useState<string>("");
  const [addInfoFlags, setAddInfoFlags] = useState<string[]>([]);
  const [addInfoFlags2, setAddInfoFlags2] = useState<string[]>([]);
  const tpUm = useRef<string>("");

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  // 한글 주석: 메뉴별 sys_addinfo(cd_flag) 목록을 조회해 js_addinfo 묶기 키로 사용한다.
  const loadAddInfoFlags = async (
    idTarget = "grid1",
    setFlags = setAddInfoFlags
  ) => {
    const result: any = await fnGetDataSet({
      id: "sys_addinfo_pop_01_01",
      params: {
        cd_firm: userInfo?.cd_firm ?? "",
        cd_menu: Menuprops?.cd_menu ?? "",
        id_target: idTarget,
      },
    });
    const rows = result?.[0] || [];
    const flags = extractAddInfoFlags(rows);
    setFlags(flags);
    return flags;
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));

        const popup_apply1 = [
          { target: "cd_cust_sales", source: "cd_cust" },
          { target: "nm_cust_sales", source: "nm_cust_sort" },
        ];
        const popup_apply2 = [
          { target: "cd_cust_pur", source: "cd_cust" },
          { target: "nm_cust_pur", source: "nm_cust_sort" },
        ];
        const grid1 = rtn.grid.grid1.map((col: any) => {
          let newCol = { ...col };
          if (col.cd_column === "cd_cust_sales") {
            newCol.popup_apply = popup_apply1;
          }
          return newCol;
        });
        const grid2 = rtn.grid.grid2.map((col: any) => {
          let newCol = { ...col };
          if (col.cd_column === "cd_cust_pur") {
            newCol.popup_apply = popup_apply2;
          }
          return newCol;
        });
        const configGrid1 = setConfigToColumns(grid1, "SAL0010");
        setColums({
          grid1: configGrid1,
          grid2: grid2,
          sheet1: rtn.grid.sheet1,
        });

        const payload = {
          id: "sotype",
          params: { cd_firm: userInfo.cd_firm },
        };
        const data = await fnGetTableById(payload);
        if (Array.isArray(data) && data.length > 0) {
          sotypeRef.current = data;
        }

        const erpIf = await callApi("getManualIf", {
          cd_menu: Menuprops?.cd_menu ?? "SN_SAL_SAO_REG_001",
        });
        erpIfRef.current = Array.isArray(erpIf?.data) ? erpIf.data : [];

        setHeaderInfo({
          grid1: filterApprovalHeader(rtn.header.grid1),
          grid2: rtn.header.grid2,
        });
        // 한글 주석: 초기 진입 시 부가항목 키 목록도 함께 로드한다.
        await loadAddInfoFlags("grid1", setAddInfoFlags);
        await loadAddInfoFlags("grid2", setAddInfoFlags2);
        const headerData = rtn.header.grid2?.data;
        tpUm.current = headerData.tp_um ?? "001";
      } catch (error) {}
    };
    fetchform();
  }, []);

  const drilldownHandledRef = useRef<any>(null);
  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      // 조회·팝업 등으로 컬럼이 다시 만들어질 때마다 드릴다운이 재실행되지 않게(처리탭으로 되돌아가는 문제) 드릴다운 1건당 1번만.
      if (drilldownHandledRef.current !== Menuprops) {
        drilldownHandledRef.current = Menuprops;
        onActivate(Menuprops.nodeData);
      }
    }
  }, [columns, Menuprops]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelUpload,
    onExcelDownLoad,
    onPrint,
  }));

  // 결재상신 / ERP 연동 버튼 표출 여부
  // ERP 연동 버튼은 DB 기본값이 fg_visible = 'N'이라 연동 업체일 때만 'Y'로 바꾼다
  const filterApprovalHeader = (header: any) => {
    if (!header) return header;
    return {
      ...header,
      controls: (header.controls || [])
        .filter(
          (ctrl: any) =>
            isApprovalVisible || ctrl.cd_param !== "aButton_Approve"
        )
        .map((ctrl: any) =>
          ctrl.cd_param === "btnInterface" && erpIfRef.current.length > 0
            ? { ...ctrl, fg_visible: "Y" }
            : ctrl
        ),
    };
  };

  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }

    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_sal_sao_reg_001_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      const data: any = await fnGetDataSet(params);

      const flags =
        addInfoFlags.length > 0
          ? addInfoFlags
          : await loadAddInfoFlags("grid1", setAddInfoFlags);

      const flags2 =
        addInfoFlags2.length > 0
          ? addInfoFlags2
          : await loadAddInfoFlags("grid2", setAddInfoFlags2);

      setDataSources({
        grid1: hydrateRowsWithAddInfo(data[0] || [], flags),
        grid2: hydrateRowsWithAddInfo(data[1] || [], flags2),
        sheet1: data[2] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      getGridCurrent("grid1")?.addRow();
      dtIsuPlanRef.current = await getPlanDt(dateToString(new Date()), -1);
    } catch (error: any) {}
  };

  const onBeforeAddRow1 = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("alter", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());

      const searchFgTrns = String(searchInfo?.fg_trns ?? "").trim();
      const sotypes: any[] = Array.isArray(sotypeRef.current)
        ? sotypeRef.current
        : [];

      let matchedSo: any;
      if (searchFgTrns) {
        matchedSo = sotypes.find(
          (so: any) =>
            String(so.fg_trns ?? "") === searchFgTrns && so.yn_use === "Y"
        );
      }

      // 조회조건 fg_trns가 비어 있으면 cd_so 콤보 top1 수주형태로 대체
      if (!matchedSo) {
        const col = columns.grid1.find((col: any) => col.cd_column === "cd_so");
        const jsCombo =
          typeof col?.js_combo === "string"
            ? JSON.parse(col.js_combo)
            : col?.js_combo;
        const firstCdSo = jsCombo?.[0]?.code;
        matchedSo = sotypes.find((so: any) => so.cd_so === firstCdSo);
      }

      const fg_trns = searchFgTrns || matchedSo?.fg_trns || "1";
      let fg_vat = "11";
      if (fg_trns === "2" || fg_trns === "3") {
        fg_vat = "14";
      } else if (fg_trns === "4" || fg_trns === "5") {
        fg_vat = "15";
      }

      const cd_so = matchedSo?.cd_so ?? "";
      const fg_money = matchedSo?.fg_money ?? "1";
      const yn_app = matchedSo?.yn_app ?? "N";
      const yn_trade = matchedSo?.yn_trade ?? "N";
      const yn_autoreq = matchedSo?.yn_autoreq ?? "N";
      const yn_autoisu = matchedSo?.yn_autoisu ?? "N";
      const yn_autosales = matchedSo?.yn_autosales ?? "N";
      const st_so = yn_app === "Y" ? "R" : "F";

      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_cust: searchInfo?.cd_cust,
        nm_cust_sort: searchInfo?.nm_cust_sort,
        cd_cust_dlv: searchInfo?.cd_cust,
        nm_cust_dlv: searchInfo?.nm_cust_sort,
        cd_dept: userInfo?.cd_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_so: today,
        dt_col_plan: today,
        fg_trns: fg_trns,
        fg_vat: fg_vat,
        rt_exch: 1,
        tp_um: "top1",
        cd_exch: "000",
        no_so: "",
        cd_so: cd_so,
        fg_money: fg_money,
        yn_app: yn_app,
        yn_trade: yn_trade,
        yn_autoreq: yn_autoreq,
        yn_autoisu: yn_autoisu,
        yn_autosales: yn_autosales,
        st_so: st_so,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRow = () => {
    try {
      getGridCurrent("grid1")?.stopEditing();
      return getGridCurrent("grid2")?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRow2 = () => {
    try {
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }
      getGridCurrent("grid1").applyBlock(focusedNodeH);

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const today = dateToString(new Date());
      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      return {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_biz: focusedNodeH.data?.cd_biz,
        cd_dept: focusedNodeH.data?.cd_dept,
        cd_gp: focusedNodeH.data?.cd_gp,
        cd_emp: focusedNodeH.data?.cd_emp,
        dt_dlv: today,
        dt_isu_plan: dtIsuPlanRef.current,
        dt_rtn_plan: today,
        no_line: maxNoLine + 1,
        no_seq_sort: maxNoLine + 1,
        no_so: focusedNodeH.data?.no_so,
        st_so: focusedNodeH.data?.st_so,
        cd_so: focusedNodeH.data?.cd_so,
        fg_trns: focusedNodeH.data?.fg_trns,
        fg_vat: focusedNodeH.data?.fg_vat,
        qt: 0,
        um: 0,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (getGridCurrent("grid1")?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    // 라인이 없으면 진행된 수량이 있을 수 없으므로 헤더는 무조건 삭제 가능
    let blockMsg: string | null = null;
    const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
    getGridCurrent("grid2").forEachFilteredNode((node: any) => {
      if (!blockMsg)
        blockMsg = checkBeforeDelete(focusedNodeH?.data, node.data);
    });
    if (blockMsg) {
      await showDialog("alter", blockMsg);
      return;
    }

    getGridCurrent("grid2")?.deleteAllRows();
    getGridCurrent("grid1")?.deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = () => {
    const rowCount = getGridCurrent("grid2")?.getDisplayedRowCount();
    if (rowCount === 0) return;

    const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
    const selectedNode = getGridCurrent("grid2").focusedRowNode();
    if (!selectedNode?.data) return;
    const blockMsg = checkBeforeDelete(focusedNodeH?.data, selectedNode.data);
    if (blockMsg) {
      showDialog("alter", blockMsg);
      return;
    }

    getGridCurrent("grid2")?.deleteSelectedRows();
    onAfterSetFooter();

    if (rowCount === 1) {
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      getGridCurrent("grid1").clearBlock(focusedRowNodeH);
    }
  };

  // 순서: 자동매출 → 자동출고 → 자동출하의뢰 → 자동승인 (신규행은 항상 삭제 가능)
  const checkBeforeDelete = (header: any, line: any): string | null => {
    if (line.state === "insert") return null;

    const isY = (v: any) => String(v ?? "N").toUpperCase() === "Y";
    const isQtZero = (v: any) => Number(v ?? 0) === 0;

    // 헤더 조회 컬럼에 yn_app이 없으므로 수주유형(sotype) 목록에서 조회
    const soType = (sotypeRef.current ?? []).find(
      (so: any) => so.cd_so === header?.cd_so
    );
    const yn_app = header?.yn_app ?? soType?.yn_app;

    // 플래그는 계층적(자동매출 Y ⊃ 자동출고 Y ⊃ 자동의뢰 Y ⊃ 자동승인 Y)이므로 가장 높은 자동처리 레벨의 다음 단계 수량 하나만 검사
    if (isY(header?.yn_autosales)) return null;
    if (isY(header?.yn_autoisu))
      return isQtZero(line.qt_cls) ? null : "매출이 진행된 건입니다.";
    if (isY(header?.yn_autoreq))
      return isQtZero(line.qt_isu) ? null : "출하가 진행된 건입니다.";
    if (isY(yn_app))
      return isQtZero(line.qt_req) ? null : "이미 진행된 건입니다.";
    const st = String(line.st_so ?? "").toUpperCase();
    return st === "P" || st === "F" ? null : "승인된 건입니다.";
  };

  const onSave = async () => {
    try {
      getGridCurrent("grid1")?.stopEditing();
      getGridCurrent("grid2")?.stopEditing();

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const sohChanges = getGridCurrent("grid1").getChange();
      const solChanges = getGridCurrent("grid2").getChange();
      const headerInserts = sohChanges.insert ?? [];
      const lineInserts = solChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        sohChanges.modifiedRowcount === 0 &&
        solChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      if (headerInserts.length !== 0) {
        const noSlip = await applySlip({
          cd_column: "no_so",
          cd_module: "SAL",
          no_line: 1,
          cd_prefix: "SOR",
          dt: headerInserts[0]?.dt_so,
          girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
          inserts: [sohChanges, solChanges],
          cd_menu: Menuprops.cd_menu,
        });
      }

      //  자동의뢰 필수값 검증 (현재 선택 헤더가 yn_autoreq=Y 인 경우만)
      const okAutoReq = await validateAutoReqRequired(solChanges);
      if (!okAutoReq) return false;

      // 한글 주석: 저장 시점에 flags 상태가 비어있으면 재조회하여 누락 저장을 방지한다.
      const flagsForSave =
        addInfoFlags.length > 0
          ? addInfoFlags
          : await loadAddInfoFlags("grid1", setAddInfoFlags);

      // 한글 주석: 저장 직전에 헤더 변경행의 동적 부가항목 컬럼값을 js_addinfo로 묶는다.
      const sohWithAddInfo = {
        ...sohChanges,
        insert: (sohChanges.insert ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave),
        })),
        update: (sohChanges.update ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave),
        })),
      };

      const flagsForSave2 = await loadAddInfoFlags("grid2", setAddInfoFlags2);
      const solWithAddInfo = {
        ...solChanges,
        insert: (solChanges.insert ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave2),
        })),
        update: (solChanges.update ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave2),
        })),
      };

      const saveData = {
        soh: sohWithAddInfo,
        sol: solWithAddInfo,
      };
      const ok = await fnSave("SN_SAL_SAO_REG_001_SAVE_01", saveData);
      if (!ok) {
        getGridCurrent("grid1").resetUpdate(true);
        getGridCurrent("grid2").resetUpdate(true);
        return false;
      }

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      return false;
    }
  };

  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        fg_search: nodeData.no_so,
        dt_from: nodeData.dt_so || nodeData.dt_dlv,
        dt_to: nodeData.dt_so || nodeData.dt_dlv,
      };
      const searchData = {
        ...formData,
        ...selectedData,
        fg_trns: "",
        cd_emp: "",
        nm_emp: "",
      };
      setFormData(searchData);
      //setFormData(selectedData);
      onSearch(searchData);
    }
  };

  const onExcelDownLoad = async () => {
    try {
      const displayedCols =
        gridRefs.current["sheet1"].current?.getAllDisplayedColumns() || [];
      const columnKeys = displayedCols.map((c: any) => c.getColId());

      // [excel-addinfo] sheet1 표시 컬럼 기준으로 addinfo(json) 값을 row에 주입
      const hydratedSheetRows = hydrateRowsForExcelAddInfo(
        dataSources.sheet1 || [],
        {
          targets: [
            // [excel-addinfo] 헤더 addinfo
            {
              flags: columnKeys,
              jsonKeys: ["js_addinfo_h", "js_addinfo"],
              priority: 1,
            },
            // [excel-addinfo] 라인 addinfo (동일 컬럼 충돌 시 라인 우선)
            {
              flags: columnKeys,
              jsonKeys: ["js_addinfo_l", "js_addinfo"],
              priority: 2,
            },
          ],
          conflictPolicy: "last-wins",
        }
      );

      // [excel-addinfo] 주입된 다운로드용 row로 sheet1 임시 교체 후 export
      gridRefs.current["sheet1"].current?.deleteAllRows?.();
      gridRefs.current["sheet1"].current?.applyTransaction({
        add: hydratedSheetRows.map((r: any) => ({ ...r })),
      });

      gridRefs.current["sheet1"].current?.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onExcelUpload = async () => {
    try {
      // MAS_SOTYPE 조회 (엑셀업로드용 수주형태 매핑)
      const params = {
        id: "sn_sal_bas_reg_003_01", // 백엔드 쿼리/프로시저 ID
        params: {
          cd_firm: userInfo?.cd_firm, // 회사코드
          cd_biz: userInfo?.cd_biz, // 사업장코드
          str_search: "", // 검색어(없으면 전체)
        },
      };

      const raw = await fnGetDataSet(params);

      const typeMap = toKeyMap(raw?.[0] ?? [], {
        key: "cd_so",
        candidates: ["CD_SO"],
      });

      // 초기값
      const provideDefaults = (row: any) => ({
        cd_firm: row.cd_firm ?? userInfo?.cd_firm,
        cd_biz: row.cd_biz ?? userInfo?.cd_biz,
        cd_dept: row.cd_dept ?? userInfo?.cd_dept,
        st_so: row.st_so ?? "F",
      });

      const modalRef = createRef<any>();
      const childRef = createRef<any>();
      const s01 = (
        <ExcelUpload_S01_Validate
          ref={childRef}
          modalRef={modalRef}
          Menuprops={Menuprops}
          grouping={[
            "no_so",
            "cd_cust",
            "dt_so",
            "cd_so",
            "cd_cust_dlv",
            "cd_cust_sales",
            "tp_um",
          ]}
          provideDefaults={provideDefaults}
          mappings={[
            {
              key: "cd_so",
              map: typeMap,
              transform: (_row: any, base: any) => {
                const fg_trns = base?.fg_trns || "1";
                let fg_vat = "11";
                if (fg_trns === "2" || fg_trns === "3") fg_vat = "14";
                else if (fg_trns === "4" || fg_trns === "5") fg_vat = "15";
                return {
                  fg_trns,
                  fg_vat,
                  st_so: base?.yn_app === "Y" ? "R" : "F",
                };
              },
            },
          ]}
        />
      );

      const result = await showPopupModal({
        title: "수주 엑셀 업로드",
        children: s01,
        width: "80%",
        height: "90VH",
        okayBtnText: "다음",
      });
      if (!result || result == "close") return;

      const ok = await openS02({
        ...result,
        controller: "SN_SAL_SAO_REG_001_SAVE_01",
        numbering: {
          fn: getNoSlipStrict,
          options: {
            cd_module: "SAL",
            no_line: 1,
            cd_prefix: "SOR",
            dateField: "dt_so",
            cd_menu: Menuprops.cd_menu,
          },
        },
        tables: { header: "sal_soh", detail: "sal_sol" },
        fieldMap: {
          headerNoField: "no_so",
          detailNoField: "no_so",
          lineNoField: "no_line",
        },
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onPrint = async () => {
    getGridCurrent("grid1").stopEditing();
    getGridCurrent("grid2").stopEditing();

    const selectedRow = getGridCurrent("grid1").getSelectedRows();
    if (selectedRow.length < 1) {
      await showDialog("alter", "선택된 수주정보가 없습니다.");
      return;
    }
    return {
      cd_firm: userInfo.cd_firm,
      cd_biz: selectedRow[0].cd_biz,
      no_so: selectedRow[0].no_so,
    };
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnApply") {
      const salSohChanges = getGridCurrent("grid1").getChange().insert;
      if (salSohChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        cd_cust: selectedFormData.cd_cust,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
      };

      const component = (
        <SN_SAL_SAO_REG_001_POP_001 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "견적적용 도움",
        children: component,
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult = result[1];

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH && headerResult[0]) {
        for (const key in headerResult[0]) {
          focusedRowNodeH.setDataValue(key, headerResult[0][key], "noEvent");
        }
        const fg_trns = headerResult[0].fg_trns || "1";
        let fg_vat = "11";
        if (fg_trns === "1") {
          fg_vat = "11";
        } else if (fg_trns === "2" || fg_trns === "3") {
          fg_vat = "14";
        } else if (fg_trns === "4" || fg_trns === "5") {
          fg_vat = "15";
        }
        focusedRowNodeH.setDataValue("fg_trns", fg_trns, "noEvent");
        focusedRowNodeH.setDataValue("fg_vat", fg_vat, "noEvent");
      }
      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              if (key === "no_line") continue;
              addedNode.setDataValue(key, result[key], "noEvent");
            }

            const fg_trns = headerResult[0].fg_trns || "1";
            let fg_vat = "11";
            if (fg_trns === "1") {
              fg_vat = "11";
            } else if (fg_trns === "2" || fg_trns === "3") {
              fg_vat = "14";
            } else if (fg_trns === "4" || fg_trns === "5") {
              fg_vat = "15";
            }
            addedNode.setDataValue("no_line_est", result.no_line, "noEvent");
            addedNode.setDataValue("fg_trns", fg_trns, "noEvent");
            addedNode.setDataValue("fg_vat", fg_vat, "noEvent");
          }
        }
      });
    }

    if (id == "btnProject") {
      const component = <SN_SAL_SAO_REG_001_POP_002 />;
      const result = await showPopupModal({
        title: "프로젝트 도움",
        children: component,
      });
      if (result === "close") return;
    }

    // ERP 연동: 마지막 연동 시점 이후 데이터를 가져온다(기간은 서버에서 결정)
    if (id == "btnInterface") {
      const confirm = await showDialog(
        "confirm",
        "ERP 데이터를 가져오시겠습니까?"
      );
      if (!confirm) return;

      // 스피너가 화면을 덮어 연동 중 중복 클릭도 막힌다
      const ok = await withMenuLoading(
        Menuprops.cd_menu ?? "SN_SAL_SAO_REG_001",
        async () => {
          for (const no_if of erpIfRef.current) {
            const res = await callApi(no_if, {});
            if (!res?.status) return false;
          }
          return true;
        }
      );
      if (!ok) return;
      await showDialog("info", "ERP 연동이 완료되었습니다.");
      await onSearch(searchRef.current?.getFormdata?.());
      return;
    }

    if (id == "aButton_Approve") {
      getGridCurrent("grid1")?.stopEditing();
      getGridCurrent("grid2")?.stopEditing();

      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      const modifiedRowsL = getGridCurrent("grid2").getChange();
      const hasModified =
        modifiedRowsH.modifiedRowcount > 0 ||
        modifiedRowsL.modifiedRowcount > 0;
      if (hasModified) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장 후 결재상신 하시겠습니까?"
        );
        if (!confirm) return;
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }

      if (!focusedNodeH?.data?.no_so) {
        await showDialog("alter", "결재상신할 수주건을 선택해 주세요.");
        return;
      }

      if (focusedNodeH.data.st_so !== "F") {
        await showDialog("alter", "확정 상태인 건만 결재상신할 수 있습니다.");
        return;
      }

      await executeApprovalSubmit({
        menuCode: "SN_SAL_SAO_REG_001",
        noRef: focusedNodeH.data.no_so,
        userInfo: {
          cd_firm: userInfo.cd_firm,
          cd_biz: userInfo.cd_biz,
          cd_dept: userInfo.cd_dept,
          cd_emp: userInfo.cd_emp,
          cd_user: userInfo.cd_user,
        },
        submitApproval,
        showDialog,
        openWindow: (url) => window.open(url),
        noUrlMessage: "결재창 URL을 수신하지 못했습니다.",
      });
      return;
    }
  };

  // 결재번호 더블클릭 시 상신된 아마란스 결재문서를 다시 연다
  const onCellDoubleClickedH = async (event: any) => {
    if (event.colDef.field !== "no_approve") return;

    await executeApprovalView({
      menuCode: "SN_SAL_SAO_REG_001",
      noRef: event.data?.no_so,
      noApprove: event.data?.no_approve,
      userInfo: {
        cd_firm: userInfo.cd_firm,
        cd_emp: userInfo.cd_emp,
        cd_user: userInfo.cd_user,
      },
      viewApproval,
      showDialog,
      openWindow: (url) => window.open(url),
    });
  };

  // 부가항목설정 팝업 호출
  // 부가항목 설정 팝업을 열고 저장 시 화면 메타/데이터를 재적용한다.
  const onOpenAddInfoPopup = async (idTarget = "grid1") => {
    // id_target에 해당하는 현재 컬럼 메타를 설정 팝업으로 전달한다.
    const columnInfos =
      idTarget === "grid2" ? columns.grid2 || [] : columns.grid1 || [];
    const saved = await openAddInfoPopup({
      cd_menu: Menuprops.cd_menu!,
      id_target: idTarget,
      columnInfos,
    });

    if (saved) {
      const rtn: any = await getInitFormData();
      setFormControlData(rtn.formControlData);
      setFormData((prev: any) => ({
        ...(rtn.formData || {}),
        ...(prev || {}),
      }));

      const popup_apply1 = [
        { target: "cd_cust_sales", source: "cd_cust" },
        { target: "nm_cust_sales", source: "nm_cust_sort" },
      ];
      const popup_apply2 = [
        { target: "cd_cust_pur", source: "cd_cust" },
        { target: "nm_cust_pur", source: "nm_cust_sort" },
      ];
      const grid1 = rtn.grid.grid1.map((col: any) => {
        let newCol = { ...col };
        if (col.cd_column === "cd_cust_sales") {
          newCol.popup_apply = popup_apply1;
        }
        return newCol;
      });
      const grid2 = rtn.grid.grid2.map((col: any) => {
        let newCol = { ...col };
        if (col.cd_column === "cd_cust_pur") {
          newCol.popup_apply = popup_apply2;
        }
        return newCol;
      });
      const configGrid1 = setConfigToColumns(grid1, "SAL0010");
      setColums({
        grid1: configGrid1,
        grid2: grid2,
        sheet1: rtn.grid.sheet1,
      });
      setHeaderInfo({
        grid1: filterApprovalHeader(rtn.header.grid1),
        grid2: rtn.header.grid2,
      });

      // 한글 주석: 항목설정 변경 후 저장 키 목록을 다시 로드한다.
      idTarget === "grid2"
        ? await loadAddInfoFlags(idTarget, setAddInfoFlags2)
        : await loadAddInfoFlags(idTarget, setAddInfoFlags);

      await onSearch(searchRef.current?.getFormdata?.());
    }
  };

  // grid1 헤더 추가 메뉴 클릭 처리
  const onGrid1ExtraHeaderMenuClick = async (event: {
    id: string;
    params: any;
  }) => {
    switch (event.id) {
      case "extra_item_setting":
        // 부가항목설정 실행
        await onOpenAddInfoPopup("grid1");
        break;
      default:
        break;
    }
  };

  const onGrid2ExtraHeaderMenuClick = async (event: {
    id: string;
    params: any;
  }) => {
    switch (event.id) {
      case "extra_item_setting":
        // 부가항목설정 실행
        await onOpenAddInfoPopup("grid2");
        break;
      default:
        break;
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
      case "applyUmBatch":
        applyUmBatch();
        break;
      case "applyRecentUm":
        applyRecentUm();
        break;
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_so = selectedRow.no_so;
      const dt_so = selectedRow.dt_so || "";
      setSelectedDocu(no_so);
      setSelectedDtDocu(dt_so);
    }
  };

  const onCellValueChangedH = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    if (colId === "no_so") {
      let dubFlag = false;
      const newNoSo = rowData.no_so;
      if (!newNoSo) return;

      getGridCurrent("grid1").forEachNode((node: RowNode) => {
        if (
          node.data.no_so === newNoSo &&
          node.data.KeyField !== rowData.KeyField
        ) {
          dubFlag = true;
        }
      });
      if (dubFlag) {
        await showDialog("alter", "이미 존재하는 수주번호입니다.");
        rowNode.setDataValue("no_so", "");
      }
    }
    if (colId === "cd_cust") {
      rowNode.setDataValue("cd_cust_dlv", rowData.cd_cust);
      rowNode.setDataValue("nm_cust_dlv", rowData.nm_cust_sort);
    }
    if (colId === "cd_so") {
      let soType: any;
      for (const so of sotypeRef.current) {
        if (so.cd_so === rowData.cd_so) soType = so;
      }
      if (soType) {
        rowNode.setDataValue("fg_trns", soType.fg_trns);
        rowNode.setDataValue("fg_money", soType.fg_money);
        rowNode.setDataValue("yn_autoreq", soType.yn_autoreq);
        rowNode.setDataValue("yn_autoisu", soType.yn_autoisu);
        rowNode.setDataValue("yn_autosales", soType.yn_autosales);
        soType.yn_app === "Y"
          ? rowNode.setDataValue("st_so", "R")
          : rowNode.setDataValue("st_so", "F");

        const fg_trns = soType.fg_trns || "1";
        let fg_vat = "11";
        if (fg_trns === "1") {
          fg_vat = "11";
        } else if (fg_trns === "2" || fg_trns === "3") {
          fg_vat = "14";
        } else if (fg_trns === "4" || fg_trns === "5") {
          fg_vat = "15";
        }
        rowNode.setDataValue("fg_vat", fg_vat);
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue("fg_trns", fg_trns, "noEvent");
          node.setDataValue("fg_vat", fg_vat, "noEvent");
        });
      } else {
        rowNode.setDataValue("fg_trns", "1");
        rowNode.setDataValue("fg_money", "1");
        rowNode.setDataValue("yn_autoreq", "N");
        rowNode.setDataValue("yn_autoisu", "N");
        rowNode.setDataValue("yn_autosales", "N");
        rowNode.setDataValue("st_so", "F");
        rowNode.setDataValue("fg_vat", "11");
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue("fg_trns", "1", "noEvent");
          node.setDataValue("fg_vat", "11", "noEvent");
        });
      }
    }
    if (colId === "st_so") {
      getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
        node.setDataValue("st_so", rowData.st_so);
      });
    }
    await CalAmBulk(
      gridRefs.current["grid1"],
      gridRefs.current["grid2"],
      colId
    );
  };

  const onCellValueChangedL = async (event: CellValueChangedEvent) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    if (colId === "cd_wh") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_wh) return;

      let apply = false;
      getGridCurrent("grid2")?.forEachFilteredNode((node: RowNode) => {
        if (
          node.data[colId] === "" ||
          node.data[colId] === null ||
          node.data[colId] === undefined
        )
          apply = true;
      });
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );

      if (confirm) {
        getGridCurrent("grid2")?.forEachFilteredNode((node: RowNode) => {
          if (
            node.data.cd_wh === "" ||
            node.data.cd_wh === null ||
            node.data.cd_wh === undefined
          ) {
            node.setDataValue("cd_wh", rowData.cd_wh);
            node.setDataValue("nm_wh", rowData.nm_wh);
          }
        });
      }
    }
    if (colId === "dt_dlv") {
      if (!rowData.dt_dlv) return;

      const dt_isu_plan = await getPlanDt(rowData.dt_dlv, -1);
      rowNode.setDataValue("dt_isu_plan", dt_isu_plan);

      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;

      let apply = false;
      getGridCurrent("grid2")?.forEachFilteredNode((node: RowNode) => {
        if (
          node.data[colId] === "" ||
          node.data[colId] === null ||
          node.data[colId] === undefined
        )
          apply = true;
      });
      if (!apply) return;

      getGridCurrent("grid2")?.forEachFilteredNode((node: RowNode) => {
        if (
          node.data[colId] === "" ||
          node.data[colId] === null ||
          node.data[colId] === undefined
        ) {
          node.setDataValue(colId, rowData[colId]);
        }
      });
    }

    await CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onAfterSetFooter = () => {
    const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
    const footerData = getGridCurrent("grid2").getFooterData();
    if (focusedNodeH) {
      focusedNodeH.setDataValue("am", footerData.am);
      focusedNodeH.setDataValue("am_k", footerData.am_k);
      focusedNodeH.setDataValue("am_vat", footerData.am_vat);
      focusedNodeH.setDataValue("am_supply", footerData.am_supply);
    }
  };

  const onAfterPopupClose = async (event: any) => {
    const applyNodes = event.nodes;
    const colId = event.colId;
    if (colId === "cd_item") {
      await setUmByFgUm(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "dt_so",
        "",
        applyNodes
      );
    }
  };

  const handleContextMenu = (params: any) => {
    // SnGrid에서 onContextMenu는 객체({ id, menu }) 형태로 전달됨
    switch (params?.id) {
      // 수주부가항목(헤더)
      case "wf_sn_sal_so_info":
        (async () => {
          const rowNode = getGridCurrent("grid1").focusedRowNode();
          if (!rowNode) {
            await showDialog("alter", "선택된 행이 없습니다.");
            return;
          }
          if (!Menuprops.cd_menu) {
            await showDialog("alter", "메뉴 코드가 없습니다.");
            return;
          }

          // id_target은 호출 메뉴에서 전달 (고정값 아님)
          const idTarget = "grid1";

          const result = await openAddInfoRegPopup({
            title: "수주부가항목",
            cd_menu: Menuprops.cd_menu,
            id_target: idTarget,
            rowData: { ...rowNode.data },
            width: "980px",
            height: "760px",
            rememberSizeKey: "SN_SAL_SAO_REG_001",
          });
          if (!result || result === "close") return;

          const values = result?.values ?? {};
          Object.keys(values).forEach((key) => {
            rowNode.setDataValue(key, values[key]);
          });
        })();
        break;
      // 수주부가항목(라인)
      case "wf_sn_sal_so_info2":
        (async () => {
          const rowNode = getGridCurrent("grid2").focusedRowNode();
          if (!rowNode) {
            await showDialog("alter", "선택된 행이 없습니다.");
            return;
          }
          if (!Menuprops.cd_menu) {
            await showDialog("alter", "메뉴 코드가 없습니다.");
            return;
          }

          // id_target은 호출 메뉴에서 전달 (고정값 아님)
          const idTarget = "grid2";

          const result = await openAddInfoRegPopup({
            title: "수주부가항목",
            cd_menu: Menuprops.cd_menu,
            id_target: idTarget,
            rowData: { ...rowNode.data },
            width: "980px",
            height: "760px",
            rememberSizeKey: "SN_SAL_SAO_REG_001",
          });
          if (!result || result === "close") return;

          const values = result?.values ?? {};
          Object.keys(values).forEach((key) => {
            rowNode.setDataValue(key, values[key]);
          });
        })();
        break;
      // 출하의뢰등록
      case "fn_sn_sal_com_isu_reg_01":
        (async () => {
          const selectedDataH = getGridCurrent("grid1").getSelectedRows();
          const selectedRows = getGridCurrent("grid2").getSelectedRows();
          if (!selectedDataH?.length) {
            await showDialog("alter", "선택된 행이 없습니다.");
            return;
          }
          let noLineString = "";
          selectedRows.forEach((row: any) => {
            noLineString += row.no_line + "|";
          });
          await fnDrilldown({
            cd_menu: params.menu ?? "SN_SAL_ISU_REG_001",
            no_docu: selectedDataH[0].no_so,
            no_line: noLineString,
          });
        })();
        break;
      default:
    }
  };

  const validateAutoReqRequired = async (salSolChanges: any) => {
    const selNodes = getGridCurrent("grid1").focusedRowNode();
    if (!selNodes) return true;

    const header = selNodes.data || {};
    const need = (header.yn_autoreq ?? "").toString().toUpperCase() === "Y";
    if (!need) return true;

    const inserts = salSolChanges?.insert ?? [];
    const updates = salSolChanges?.update ?? [];

    // 컬럼명 → 타이틀 매핑 ===
    const colTitleMap: Record<string, string> = {};
    columns.grid2.forEach((col: any) => {
      colTitleMap[col.cd_column] = col.nm_column;
    });

    const missing: string[] = [];

    const check = (row: any, idx: number, kind: "insert" | "update") => {
      const cd_wh = (row?.cd_wh ?? "").toString().trim();
      const dt_dlv = (row?.dt_dlv ?? "").toString().trim();

      const cd_wh_title = colTitleMap["cd_wh"] || "cd_wh";
      const dt_dlv_title = colTitleMap["dt_dlv"] || "dt_dlv";

      if (!cd_wh) missing.push(`[${kind}] ${cd_wh_title} 누락 (row#${idx})`);
      if (!dt_dlv) missing.push(`[${kind}] ${dt_dlv_title} 누락 (row#${idx})`);
    };

    inserts.forEach((r: any, i: number) => check(r, i + 1, "insert"));
    updates.forEach((r: any, i: number) => check(r, i + 1, "update"));

    if (missing.length > 0) {
      await showDialog(
        "error",
        "자동의뢰(yn_autoreq=Y) 시 필수값 누락:\n" + missing.join("\n")
      );
      return false;
    }
    return true;
  };

  const applyUmBatch = async () => {
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
    if (rowCount === 0) return;

    try {
      let flag = false;
      getGridCurrent("grid2").forEachFilteredNode((node: any) => {
        if (node.data.st_so === "R") {
          flag = true;
        }
      });
      if (flag) {
        await showDialog(
          "alter",
          "승인된 건이 존재합니다.\r\n승인취소 후 진행해주시기 바랍니다."
        );
        return;
      }

      await setUmByFgUm(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "dt_so",
        tpUm.current
      );

      await CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "rt_exch"
      );
    } catch (error) {
      console.error(error);
    }
  };

  const applyRecentUm = async () => {
    const focusedNodeH = getGridCurrent("grid1").focusedRowNode();

    let flag = false;
    let displayedNodes: any[] = [];
    getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
      displayedNodes.push(node);
      if (node.data.st_so === "R") {
        flag = true;
      }
    });
    if (flag) {
      await showDialog(
        "alter",
        "승인된 건이 존재합니다.\r\n승인취소 후 진행해주시기 바랍니다."
      );
      return;
    }

    const itemSet = new Set<string>();
    displayedNodes.forEach((node) => itemSet.add(node.data.cd_item));
    const cdItemList = Array.from(itemSet).join(",");

    const params = {
      id: "sn_sal_sao_reg_001_recentUm",
      params: {
        cd_firm: userInfo.cd_firm,
        cd_biz: focusedNodeH.data.cd_biz,
        cd_item: cdItemList,
        cd_cust: focusedNodeH.data.cd_cust,
        cd_exch: focusedNodeH.data.cd_exch,
      },
    };
    const data: any = await fnGetDataSet(params);

    const umMap = new Map<string, number>();
    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
      data[0].forEach((row: any) => {
        umMap.set(row.cd_item, row.um);
      });
    }

    for (const node of displayedNodes) {
      if (!node?.data) continue;

      const cdItem = node.data.cd_item;
      const umValue = umMap.get(cdItem) ?? 0;
      if (umValue !== undefined && umValue !== 0) {
        node.setDataValue("um", umValue);
      }
    }

    await CalAmBulk(
      gridRefs.current["grid1"],
      gridRefs.current["grid2"],
      "rt_exch"
    );
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onCellValueChanged: onCellValueChangedH,
          onCellDoubleClicked: onCellDoubleClickedH,
          onContextMenu: handleContextMenu,
          onBeforeAddRow: onBeforeAddRow1,
          onBtnEtcClick: onGrid1BtnsClick,
          onExtraHeaderMenuClick: onGrid1ExtraHeaderMenuClick,
        },
        headerInfo: headerInfo.grid1,
        filter: { refs: ["grid2"], columns: ["no_so"] },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChangedL,
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onContextMenu: handleContextMenu,
          onBeforeAddRow: onBeforeAddRow2,
          onAfterSetFooter: onAfterSetFooter,
          onAfterPopupClose: onAfterPopupClose,
          onBtnEtcClick: onGrid2BtnsClick,
          onExtraHeaderMenuClick: onGrid2ExtraHeaderMenuClick,
          onHeaderValueChanged: (event: any) => {
            if (event.colId === "tp_um") {
              tpUm.current = event.newValue;
            }
          },
        },
        headerInfo: headerInfo.grid2,
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources, headerInfo]
  );

  const search = useMemo(
    () => ({
      data: formData,
      controls: formControlData,
    }),
    [formData, formControlData]
  );

  const fileUpload = useMemo(
    () => ({
      fileUplaod1: {
        selectedDocu: selectedDocu,
        selectedLineDocu: 0,
        selectedDtDocu: selectedDtDocu,
      },
    }),
    [selectedDocu, selectedDtDocu]
  );

  const dynamicProps = React.useMemo(
    () => ({
      searchRef,
      search,
      grids,
      gridRefs,
      fileUpload,
    }),
    [formData, formControlData, grids, gridRefs, fileUpload]
  );
  //region UI
  return <ComCreateUi dynamicProps={dynamicProps} layoutConfig={layout} />;
});

export default SN_SAL_SAO_REG_001;
